import AchievementModal from '@/components/AchievementModal';
import CategorySection from '@/components/CategorySection';
import CategorySectionTopBar from '@/components/CategorySectionTopBar';
import StudentViewSection from '@/components/StudentViewSection';
import Constants from '@/Constants';
import { useContentLanguageChange } from '@/hooks/useContentLanguageChange';
import { useGet } from '@/hooks/useGet';
import { useSystemLanguageChange } from '@/hooks/useSystemLanguageChange';
import { PersonBadge } from '@/models/PersonBadge';
import { FullConfigurationWithExtraData } from '@/models/SWRCache';
import { ViewType, viewTypeList } from '@/models/ViewType';
import { getDisplayName } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { getCategoriesRequest, getCategoryClear } from '@/store/category/action';
import { clearModal } from '@/store/common/actions';
import { getFavoriteListRequest } from '@/store/favorite/actions';
import { contentsClear } from '@/store/item/action';
import { coursesClear } from '@/store/list/action';
import { programsClear } from '@/store/program/action';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSWRConfig } from 'swr';
import './style.scss';

const Home: React.FC = () => {
    const { strings, person, orgId, applicationId, personId, mustChangePassword } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        mustChangePassword: state.identity.data.user.mustChangePassword,
        person: state.identity.data.person,
        orgId: state.identity.data.person.orgId,
        applicationId: state.identity.data.person.applicationId,
        personId: state.identity.data.person.id || 0,
    }));

    const [internalViewTypeList, setInternalViewTypeList] = useState<ViewType[]>(viewTypeList);

    const { data } = useGet<PersonBadge[]>(`${Constants.CAST_GET_ACHIEVEMENTS_URI.replace('%p', personId.toString())}?onlyPendingNotifications=true`);

    const [achievementsData, setAchievementsData] = useState<PersonBadge[]>([]);

    useEffect(() => {
        if (data) {
            setAchievementsData(data);
        }
    }, [data]);

    const setAchievementAsRead = (personBadgeId: number) => {
        setAchievementsData((oldState) => oldState.filter((it) => it.id !== personBadgeId));
    };

    const { cache } = useSWRConfig() as FullConfigurationWithExtraData;

    const dispatch = useDispatch();

    const clearAll = (): void => {
        dispatch(getCategoryClear());
        dispatch(coursesClear());
        dispatch(contentsClear());
        dispatch(programsClear());
        setInternalViewTypeList([]);
        cache.clear();
    };

    useContentLanguageChange({
        onChange: () => {
            dispatch(getCategoriesRequest({ orgId, applicationId, onlyIfHasCourseOrContent: true, limit: 0 }));
            dispatch(getFavoriteListRequest());
            dispatch(clearModal());
        },
        unmountChange: () => clearAll(),
    });

    useSystemLanguageChange({
        onChange: () => {
            dispatch(getCategoriesRequest({ orgId, applicationId, onlyIfHasCourseOrContent: true, limit: 0 }));
        },
    });

    useEffect(() => {
        if (internalViewTypeList.length === 0) {
            setInternalViewTypeList(viewTypeList);
        }
    }, [internalViewTypeList]);

    return (
        <>
            {!mustChangePassword && achievementsData && achievementsData.length > 0 && (
                <AchievementModal achievement={achievementsData[0]} setAchievementAsRead={setAchievementAsRead} />
            )}
            <div className="main">
                <div className="topBarSection">
                    <CategorySectionTopBar />
                </div>
                <div className="container welcome-msg__text">
                    <h2>{`${strings.HOME_HELLO}, ${getDisplayName(person, true)}!`}</h2>
                </div>
                <div className="courseAndContentSection">
                    {internalViewTypeList.map((viewType: ViewType) => {
                        if (viewType === 'listOfCategories') {
                            return <CategorySection categorySectionTitle={strings.LIST_OF_CATEGORIES} key={viewType} />;
                        }
                        return <StudentViewSection key={viewType} randomizeRecommendations={true} simplified viewType={viewType} />;
                    })}
                </div>
            </div>
        </>
    );
};

export default Home;
